import React, { useMemo, memo, useRef, useCallback, useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TableSortLabel,
  Box,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import useMatrixScale from '../hooks/useMatrixScale';
import useThrottledValue from '../hooks/useThrottledValue';
import { formatTuple, CELL_TEXT_SX, staleTickers, staleTitle, STALE_TICKER_COLOR } from '../utils/matrixFormat';
import useLiveMatrixData from '../hooks/useLiveMatrixData';
import { medianRowPercentile, medPercDelta, formatDelta, deltaColor, deltaTitle } from '../utils/medPerc';
import {
  pairZ, dollarZ, medianRowZ, cheapAndRank, yieldBasis, compareNullsLast,
  formatZ, formatCheapBp, formatRank, medZTitle, cheapTitle, rankTitle, coverageTitle,
} from '../utils/matrixStats';

// PRE-COMPUTED COLOR PALETTES - O(1) array lookup, no RGB calculations at runtime
// Green shades (0-50%: cheap/buy signal) - darker = more extreme percentile
const GREEN_PALETTE_LIGHT = [
  '#1a6b05', // 0-10%   - darkest green (strongest buy)
  '#2d8a0e', // 10-20%
  '#47a825', // 20-30%
  '#6bc44a', // 30-40%
  '#98d977', // 40-50% - lightest green (approaching fair value)
];

// Dark mode uses SAME colors as light mode for consistent trading signals
const GREEN_PALETTE_DARK = [
  '#1a6b05', // 0-10%   - darkest green (strongest buy)
  '#2d8a0e', // 10-20%
  '#47a825', // 20-30%
  '#6bc44a', // 30-40%
  '#98d977', // 40-50% - lightest green (approaching fair value)
];

// Red shades (50-100%: expensive/sell signal) - darker = more extreme
const RED_PALETTE_LIGHT = [
  '#f5c4c4', // 50-60% - lightest red (just past fair value)
  '#e99a9a', // 60-70%
  '#d66a6a', // 70-80%
  '#c43d3d', // 80-90%
  '#8c1414', // 90-100% - darkest red (strongest sell)
];

// Dark mode uses SAME colors as light mode for consistent trading signals
const RED_PALETTE_DARK = [
  '#f5c4c4', // 50-60% - lightest red (just past fair value)
  '#e99a9a', // 60-70%
  '#d66a6a', // 70-80%
  '#c43d3d', // 80-90%
  '#8c1414', // 90-100% - darkest red (strongest sell)
];

// Neutral color for 50% (fair value) - same for both modes
const NEUTRAL_LIGHT = '#f5f5f5';
const NEUTRAL_DARK = '#f5f5f5';

// Fast percentile to color lookup - O(1) array index access
const getColorForPercentile = (percentile, isDarkMode = false) => {
  if (percentile === null || percentile === undefined) {
    return isDarkMode ? '#2a2a2a' : '#ffffff';
  }

  const p = Math.max(0, Math.min(100, percentile));

  // Fair value zone (48-52%)
  if (p >= 48 && p <= 52) {
    return isDarkMode ? NEUTRAL_DARK : NEUTRAL_LIGHT;
  }

  if (p < 50) {
    // Green zone (buy signal) - 0-48%
    const greenPalette = isDarkMode ? GREEN_PALETTE_DARK : GREEN_PALETTE_LIGHT;
    const idx = Math.min(Math.floor(p / 10), greenPalette.length - 1);
    return greenPalette[idx];
  } else {
    // Red zone (sell signal) - 52-100%
    const redPalette = isDarkMode ? RED_PALETTE_DARK : RED_PALETTE_LIGHT;
    const idx = Math.min(Math.floor((p - 50) / 10), redPalette.length - 1);
    return redPalette[idx];
  }
};

// Cell dimensions come from useMatrixScale, published as CSS custom properties
// on the TableContainer. The styled() components below read var(--mx-*) rather
// than taking props, so their Emotion class names stay stable as the scale
// changes — otherwise every cell would get a fresh class on every resize.
const CELL_WIDTH = 'var(--mx-cell-w)';
const CELL_HEIGHT = 'var(--mx-cell-h)';
const ROW_HEADER_WIDTH = 'var(--mx-row-header-w)';
const SUMMARY_COL_WIDTH = 'var(--mx-summary-w)';
const FONT_MAIN = 'var(--mx-font)';
const FONT_SUB = 'var(--mx-font-sm)';
const FONT_HEADER = 'var(--mx-font-header)';
const CELL_PAD = 'var(--mx-cell-pad)';

// Styled components - NO transitions, CSS containment for performance
// Matching VirtualizedSpreadMatrix styling exactly
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: CELL_PAD,
  fontSize: FONT_MAIN,
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  contain: 'layout style paint', // Isolate layout/style AND clip painting to the cell box
  minWidth: CELL_WIDTH,
  maxWidth: CELL_WIDTH,
  height: CELL_HEIGHT,
  transition: 'background-color 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // Flash animation for live value updates — slow + thick so the eye catches it.
  '@keyframes cellFlash': {
    '0%':   { boxShadow: 'inset 0 0 0 3px #ffeb3b, 0 0 12px rgba(255,235,59,0.85)' },
    '50%':  { boxShadow: 'inset 0 0 0 3px #ffeb3b, 0 0 8px rgba(255,235,59,0.55)' },
    '100%': { boxShadow: 'inset 0 0 0 0 rgba(255,235,59,0), 0 0 0 rgba(255,235,59,0)' },
  },
  '&.cell-flash': {
    animation: 'cellFlash 1.6s ease-out',
  },
}));

// Column positions for sticky behavior — calc() because the widths are now
// custom properties rather than numbers.
const TICKER_LEFT = 0;
const PRICE_LEFT = `calc(${ROW_HEADER_WIDTH})`;
const STRIP_LEFT = `calc(${ROW_HEADER_WIDTH} + ${SUMMARY_COL_WIDTH})`;
const MEDPERC_LEFT = `calc(${ROW_HEADER_WIDTH} + 2 * ${SUMMARY_COL_WIDTH})`;
const DELTA_LEFT = `calc(${ROW_HEADER_WIDTH} + 3 * ${SUMMARY_COL_WIDTH})`;
const MEDZ_LEFT = `calc(${ROW_HEADER_WIDTH} + 4 * ${SUMMARY_COL_WIDTH})`;
const CHEAP_LEFT = `calc(${ROW_HEADER_WIDTH} + 5 * ${SUMMARY_COL_WIDTH})`;
const RANK_LEFT = `calc(${ROW_HEADER_WIDTH} + 6 * ${SUMMARY_COL_WIDTH})`;

const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
  color: theme.palette.text.primary,
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: CELL_PAD,
  fontSize: FONT_HEADER,
  position: 'sticky',
  top: 0,
  zIndex: 10,
  minWidth: CELL_WIDTH,
  maxWidth: CELL_WIDTH,
}));

// Ticker column header - sticky both top AND left
const TickerHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
  color: theme.palette.text.primary,
  borderRight: `2px solid ${theme.palette.divider}`,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: CELL_PAD,
  fontSize: FONT_HEADER,
  position: 'sticky',
  top: 0,
  left: TICKER_LEFT,
  zIndex: 20, // Higher z-index for corner cells
  minWidth: ROW_HEADER_WIDTH,
  maxWidth: ROW_HEADER_WIDTH,
}));

const TickerCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
  color: theme.palette.text.primary,
  borderRight: `2px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: CELL_PAD,
  fontSize: FONT_MAIN,
  position: 'sticky',
  left: TICKER_LEFT,
  zIndex: 5,
  minWidth: ROW_HEADER_WIDTH,
  maxWidth: ROW_HEADER_WIDTH,
}));

// Summary cells (Price, Strip) - sticky left
const SummaryCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[50],
  color: theme.palette.text.primary,
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: CELL_PAD,
  fontSize: FONT_HEADER,
  position: 'sticky',
  zIndex: 5,
  minWidth: SUMMARY_COL_WIDTH,
  maxWidth: SUMMARY_COL_WIDTH,
  height: CELL_HEIGHT,
}));

// Sortable header cells for summary columns - sticky both top AND left
const SortableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
  color: theme.palette.text.primary,
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: '2px 4px',
  fontSize: FONT_HEADER,
  position: 'sticky',
  top: 0,
  zIndex: 20, // Higher z-index for header cells that are also sticky left
  cursor: 'pointer',
  minWidth: SUMMARY_COL_WIDTH,
  maxWidth: SUMMARY_COL_WIDTH,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[200],
  },
}));

// Removed SearchCell - search boxes moved to Dashboard header

// ============ MEMOIZED ROW COMPONENT ============
// Each row only re-renders when its specific data changes
// This is CRITICAL for 98-ticker (9604 cell) performance

// Pre-computed styles to avoid creating objects on each render
const priceBoldStyle = { fontWeight: 500, fontSize: FONT_HEADER };
// Cell text: tabular figures so digits line up, nowrap + hidden so a long value
// is clipped at the cell border instead of painted over its neighbour.
const cellTextStyle = { ...CELL_TEXT_SX, lineHeight: 1.15 };
const cellSubStyle = { ...CELL_TEXT_SX, fontSize: FONT_SUB, opacity: 0.7, lineHeight: 1.15 };

const MatrixRowInner = ({
  ticker1,
  tickers,
  matrixData,
  priceData,
  accrual,
  isDarkMode,
  spreadType,
  onCellClick,
  formatSpread,
  formatNumber,
  getSpreadValue,
  getCellPercentile,
  getSpreadField,
  textColor,
  highlightRow,
  highlightCol,
  referenceMed,
  referenceSession,
  staleSince,
  getCellZ,
  cheapBp,
  rank,
  basis,
  coverage,
}) => {
  // Track previous spread values for flash animation (lightweight: one ref per row)
  const prevValuesRef = useRef({});
  const cellRefsMap = useRef({});

  // After render, check which cells changed and trigger flash
  useEffect(() => {
    const prev = prevValuesRef.current;
    const next = {};
    tickers.forEach(ticker2 => {
      if (ticker1 === ticker2) return;
      const cellData = matrixData[ticker1]?.[ticker2];
      const val = getSpreadValue(cellData);
      const key = ticker2;
      next[key] = val;
      if (prev[key] !== undefined && prev[key] !== val && val !== null) {
        const el = cellRefsMap.current[key];
        if (el) {
          el.classList.remove('cell-flash');
          // Force reflow to restart animation
          void el.offsetWidth;
          el.classList.add('cell-flash');
        }
      }
    });
    prevValuesRef.current = next;
  }, [tickers, ticker1, matrixData, getSpreadValue]);

  // Build tooltip strings once per row
  const buildPriceTooltip = (ticker, pd) => {
    if (!pd) return ticker;
    return `${ticker}\nPrice: $${pd.price?.toFixed(2) || '-'}${pd.kind ? ` (${pd.kind}${pd.kindDate ? ` ${pd.kindDate}` : ''})` : ''}\nYield: ${pd.currentYield?.toFixed(2) || '-'}%\nYTM: ${pd.ytm?.toFixed(2) || '-'}%\nYTC: ${pd.ytc?.toFixed(2) || '-'}%`;
  };

  // Build rich tooltip content showing current level and 2 levels above/below with colors
  // Lightweight plain-text tooltip for native title attribute (no JSX overhead)
  const buildCellTooltipText = (t1, t2, cellData) => {
    if (!cellData) return `${t1} vs ${t2}`;
    const spreadField = getSpreadField(cellData);
    if (!spreadField) return `${t1} vs ${t2}\nClick for chart`;
    const current = spreadField.current;
    const mean = spreadField.avg;
    const p5 = spreadField.p5;
    const p95 = spreadField.p95;
    const percentile = spreadField.percentile;
    let text = `${t1} vs ${t2}`;
    if (current != null) text += `\nCurrent: ${formatSpread(current)}`;
    if (mean != null) text += `\nMean:    ${formatSpread(mean)}`;
    if (p5 != null) text += `\n5%ile:   ${formatSpread(p5)}`;
    if (p95 != null) text += `\n95%ile:  ${formatSpread(p95)}`;
    if (percentile != null) text += `\nCurrent %ile: ${percentile.toFixed(1)}%`;
    text += '\nClick for chart';
    return text;
  };

  const buildCellTooltipContent = (t1, t2, cellData) => {
    if (!cellData) return null;
    const spreadField = getSpreadField(cellData);
    if (!spreadField) return (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="caption">{t1} vs {t2}</Typography>
        <Typography variant="caption" display="block" color="text.secondary">Click for chart</Typography>
      </Box>
    );

    const current = spreadField.current;
    const percentile = spreadField.percentile;

    // Get levels from the data (level1, level2, level3 correspond to ~20%, 50%, 80%)
    const level1 = spreadField.level1;
    const level2 = spreadField.level2;
    const level3 = spreadField.level3;

    // Build levels array with percentiles
    const allLevels = [];
    if (level1 !== undefined && level1 !== null) allLevels.push({ value: level1, percentile: 20 });
    if (level2 !== undefined && level2 !== null) allLevels.push({ value: level2, percentile: 50 });
    if (level3 !== undefined && level3 !== null) allLevels.push({ value: level3, percentile: 80 });

    // Sort by value
    allLevels.sort((a, b) => a.value - b.value);

    // Find where current value falls and get 2 above/below
    let above = [];
    let below = [];

    if (allLevels.length > 0 && current !== undefined && current !== null) {
      let insertIndex = 0;
      for (let i = 0; i < allLevels.length; i++) {
        if (current >= allLevels[i].value) {
          insertIndex = i + 1;
        } else {
          break;
        }
      }
      below = allLevels.slice(Math.max(0, insertIndex - 2), insertIndex).reverse();
      above = allLevels.slice(insertIndex, insertIndex + 2);
    }

    // Get color for a percentile level
    const getLevelColor = (pct) => {
      if (pct <= 20) return '#1a6b05'; // dark green
      if (pct <= 40) return '#47a825'; // green
      if (pct <= 60) return '#999'; // neutral
      if (pct <= 80) return '#d66a6a'; // red
      return '#8c1414'; // dark red
    };

    return (
      <Box sx={{ p: 0.5, minWidth: 120 }}>
        <Typography variant="caption" fontWeight="bold">{t1} vs {t2}</Typography>

        {/* Levels above (expensive - red) */}
        {above.map((lvl, i) => (
          <Box key={`above-${i}`} sx={{ display: 'flex', justifyContent: 'space-between', color: getLevelColor(lvl.percentile) }}>
            <Typography variant="caption">↑ {lvl.percentile}%:</Typography>
            <Typography variant="caption" fontWeight="bold">{formatSpread(lvl.value)}</Typography>
          </Box>
        ))}

        {/* Current level */}
        {current !== undefined && current !== null && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            backgroundColor: 'rgba(255,255,255,0.2)',
            px: 0.5,
            borderRadius: 0.5,
            my: 0.25
          }}>
            <Typography variant="caption" fontWeight="bold">→ {percentile?.toFixed(0) || '?'}%:</Typography>
            <Typography variant="caption" fontWeight="bold">{formatSpread(current)}</Typography>
          </Box>
        )}

        {/* Levels below (cheap - green) */}
        {below.map((lvl, i) => (
          <Box key={`below-${i}`} sx={{ display: 'flex', justifyContent: 'space-between', color: getLevelColor(lvl.percentile) }}>
            <Typography variant="caption">↓ {lvl.percentile}%:</Typography>
            <Typography variant="caption" fontWeight="bold">{formatSpread(lvl.value)}</Typography>
          </Box>
        ))}

        <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>Click for chart</Typography>
      </Box>
    );
  };

  const isHighlighted = highlightRow === ticker1;

  const medianPercentile = medianRowPercentile(ticker1, tickers, matrixData[ticker1], getCellPercentile);
  const delta = medPercDelta(medianPercentile, referenceMed);
  const medZ = medianRowZ(ticker1, tickers, matrixData[ticker1], getCellZ);

  // Strip price = clean price = live market price - accrued dividend.
  //
  // The WebSocket price feed carries `price` but NOT `cleanPrice`, so reading
  // cleanPrice alone left this column frozen at whatever the last full matrix
  // fetch returned while Price ticked beside it. Accrual is daily data and is
  // already in the matrix payload's `yields`, so subtracting it from the live
  // price gives a genuinely live strip at zero extra server cost.
  const stripPrice = (priceData?.price != null && accrual != null)
    ? priceData.price - accrual
    : (priceData?.cleanPrice ?? null);

  return (
    <TableRow>
      {/* Column 0: Ticker (row header) */}
      <TickerCell
        component="th"
        scope="row"
        data-ticker-header={ticker1}
        title={[staleSince ? staleTitle(ticker1, staleSince) : null, coverage].filter(Boolean).join('\n') || undefined}
        sx={{
          ...(staleSince && { color: STALE_TICKER_COLOR, fontStyle: 'italic' }),
          // Highlight row ticker when it's the search row
          ...(isHighlighted && {
            outline: '2px solid #1976d2',
            outlineOffset: '-2px',
            backgroundColor: 'rgba(25, 118, 210, 0.15) !important',
          }),
        }}
      >
        {ticker1}
      </TickerCell>
      {/* Column 1: Price - sticky left */}
      <SummaryCell align="center" title={buildPriceTooltip(ticker1, priceData)} sx={{ left: PRICE_LEFT }}>
        <span style={priceBoldStyle}>
          {priceData?.price ? priceData.price.toFixed(2) : '-'}
        </span>
      </SummaryCell>
      {/* Column 2: Strip (clean price) - sticky left */}
      <SummaryCell align="center" sx={{ left: STRIP_LEFT }}>
        <span style={priceBoldStyle}>
          {stripPrice ? stripPrice.toFixed(2) : '-'}
        </span>
      </SummaryCell>
      {/* Column 3: Med% (median percentile) - sticky left */}
      <SummaryCell
        align="center"
        sx={{
          left: MEDPERC_LEFT,
          backgroundColor: getColorForPercentile(medianPercentile, isDarkMode),
        }}
      >
        <span style={{ fontWeight: 'bold', fontSize: FONT_HEADER, color: '#1a1a1a' }}>
          {medianPercentile !== null ? `${medianPercentile.toFixed(2)}%` : '-'}
        </span>
      </SummaryCell>
      {/* Column 4: Δ Med% vs the reference close - sticky left */}
      <SummaryCell
        align="center"
        title={deltaTitle(referenceSession)}
        sx={{ left: DELTA_LEFT }}
      >
        <span style={{ fontWeight: 'bold', fontSize: FONT_HEADER, color: deltaColor(delta, isDarkMode) }}>
          {referenceSession ? formatDelta(delta) : '\u2026'}
        </span>
      </SummaryCell>
      {/* Column 5: Med Z - median over peers of each pair's z-score (notebook med-z) */}
      <SummaryCell align="center" title={medZTitle(spreadType)} sx={{ left: MEDZ_LEFT }}>
        <span style={{ fontWeight: 'bold', fontSize: FONT_HEADER }}>
          {formatZ(medZ)}
        </span>
      </SummaryCell>
      {/* Column 6: Cheap bp - today's yield minus its own historical mean (notebook cheap-bp) */}
      <SummaryCell align="center" title={cheapTitle(basis)} sx={{ left: CHEAP_LEFT }}>
        <span style={{ fontWeight: 'bold', fontSize: FONT_HEADER, color: deltaColor(cheapBp === null ? null : -cheapBp / 100, isDarkMode) }}>
          {formatCheapBp(cheapBp)}
        </span>
      </SummaryCell>
      {/* Column 7: Rank by Cheap bp, 1 = cheapest (notebook rank) */}
      <SummaryCell align="center" title={rankTitle(basis)} sx={{ left: RANK_LEFT, borderRight: `2px solid #ccc` }}>
        <span style={{ fontWeight: 'bold', fontSize: FONT_HEADER }}>
          {formatRank(rank)}
        </span>
      </SummaryCell>
      {/* Columns 8+: Matrix cells */}
      {tickers.map(ticker2 => {
        const cellData = matrixData[ticker1]?.[ticker2];
        const isIdentical = ticker1 === ticker2;
        const cellPercentile = getCellPercentile(cellData);
        const isColHighlighted = highlightCol === ticker2;
        const isCellHighlighted = isHighlighted && isColHighlighted;
        const backgroundColor = isIdentical
          ? (isDarkMode ? '#616161' : '#e0e0e0')
          : getColorForPercentile(cellPercentile, isDarkMode);

        // Box highlight style for selected cell
        const cellStyle = {
          backgroundColor,
          color: '#1a1a1a',
          fontWeight: isIdentical ? 'bold' : 'normal',
          cursor: isIdentical ? 'default' : 'pointer',
          // Box highlight with thick border for matched cell
          ...(isCellHighlighted && {
            outline: '3px solid #1976d2',
            outlineOffset: '-3px',
            boxShadow: '0 0 8px rgba(25, 118, 210, 0.6)',
          }),
        };

        const spreadField = getSpreadField(cellData);
        // "(p5, mean, p95)" in compact form ("-.62" not "-0.62") so it fits the
        // cell instead of spilling over the border into its neighbours.
        const tupleStr = spreadField
          ? formatTuple(spreadField.p5, spreadField.avg, spreadField.p95, spreadType)
          : '';

        const cellContent = isIdentical ? '-' : (
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1, maxWidth: '100%', overflow: 'hidden' }}>
            {/* Top: (p5,mean,p95) tuple — numbers only */}
            <span style={cellSubStyle}>
              {tupleStr}
            </span>
            {/* Middle: the headline value */}
            <span style={cellTextStyle}>
              {formatSpread(getSpreadValue(cellData))}
            </span>
            {/* Bottom: current percentile on its own line — appended to the
                value it did not fit, and got clipped mid-number. */}
            {spreadType !== 'percentile' && cellPercentile !== null && (
              <span style={cellSubStyle}>({cellPercentile.toFixed(1)}%)</span>
            )}
          </span>
        );

        return isIdentical ? (
          <StyledTableCell
            key={ticker2}
            align="center"
            style={cellStyle}
          >
            {cellContent}
          </StyledTableCell>
        ) : (
          <StyledTableCell
            key={ticker2}
            align="center"
            onClick={() => onCellClick(ticker1, ticker2)}
            style={cellStyle}
            data-row={ticker1}
            data-col={ticker2}
            ref={el => { cellRefsMap.current[ticker2] = el; }}
          >
            {cellContent}
          </StyledTableCell>
        );
      })}
    </TableRow>
  );
};

// Custom comparison function for memo - only compare what actually affects rendering
const areRowPropsEqual = (prevProps, nextProps) => {
  // Quick reference equality checks for stable props
  if (prevProps.ticker1 !== nextProps.ticker1) return false;
  if (prevProps.isDarkMode !== nextProps.isDarkMode) return false;
  if (prevProps.spreadType !== nextProps.spreadType) return false;
  if (prevProps.textColor !== nextProps.textColor) return false;
  if (prevProps.highlightRow !== nextProps.highlightRow) return false;
  if (prevProps.highlightCol !== nextProps.highlightCol) return false;

  // Compare tickers array - check reference first, then length (most common change)
  if (prevProps.tickers !== nextProps.tickers) {
    // If reference changed, check if content is same (arrays may be rebuilt)
    if (prevProps.tickers.length !== nextProps.tickers.length) return false;
    // For large arrays, just check first/last/length (performance tradeoff)
    if (prevProps.tickers[0] !== nextProps.tickers[0]) return false;
    const lastIdx = prevProps.tickers.length - 1;
    if (lastIdx > 0 && prevProps.tickers[lastIdx] !== nextProps.tickers[lastIdx]) return false;
  }

  // Compare this row's data in matrixData
  const prevRowData = prevProps.matrixData[prevProps.ticker1];
  const nextRowData = nextProps.matrixData[nextProps.ticker1];
  if (prevRowData !== nextRowData) return false;

  // Accrual feeds the Strip column; it changes daily, not per tick.
  if (prevProps.accrual !== nextProps.accrual) return false;
  if (prevProps.referenceMed !== nextProps.referenceMed) return false;
  if (prevProps.referenceSession !== nextProps.referenceSession) return false;
  if (prevProps.staleSince !== nextProps.staleSince) return false;
  if (prevProps.cheapBp !== nextProps.cheapBp) return false;
  if (prevProps.rank !== nextProps.rank) return false;
  if (prevProps.basis !== nextProps.basis) return false;
  if (prevProps.coverage !== nextProps.coverage) return false;
  if (prevProps.getCellZ !== nextProps.getCellZ) return false;

  // Compare priceData for this row
  const prevPrice = prevProps.priceData;
  const nextPrice = nextProps.priceData;
  if (prevPrice !== nextPrice) {
    // Deep check only relevant price fields
    if (!prevPrice || !nextPrice) return false;
    if (prevPrice.price !== nextPrice.price) return false;
    if (prevPrice.currentYield !== nextPrice.currentYield) return false;
    if (prevPrice.ytm !== nextPrice.ytm) return false;
    if (prevPrice.ytc !== nextPrice.ytc) return false;
    if (prevPrice.kind !== nextPrice.kind) return false;
    if (prevPrice.kindDate !== nextPrice.kindDate) return false;
  }

  // Functions are stable due to useCallback in parent
  return true;
};

const MatrixRow = memo(MatrixRowInner, areRowPropsEqual);

const SpreadMatrix = ({
  tickers,
  matrix,
  granularity,
  spreadType = 'percentile',
  onCellClick,
  onCellHover,
  onCellHoverEnd,
  livePrices: rawLivePrices = {},
  yields = null,     // per-ticker { price, clean_price, accrual, ... } from the matrix payload
  searchRow = null,  // External search control (from Dashboard)
  searchCol = null,  // External search control (from Dashboard)
  getScaleFactor = null,  // (t1, t2) => number, for dollar spread scaling A - s*B
  referenceMedPerc = null,  // ticker -> Med% at the reference close, for the active spread type
  referenceSession = null,  // 'YYYY-MM-DD' of that close; null while loading
}) => {
  // Prices arrive several times a second; recomputing every cell that often is
  // pure waste for updates nobody can read. One update per second is plenty.
  const livePrices = useThrottledValue(rawLivePrices, 1000);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const tableContainerRef = useRef(null);

  // Automatic sizing from viewport + pixel ratio; published as CSS custom
  // properties on the TableContainer below.
  const metrics = useMatrixScale();

  // Accrued dividend per ticker, from the matrix payload. Daily data, so this
  // memo only recomputes when a new matrix arrives — never on a price tick.
  const accrualByTicker = useMemo(() => {
    const out = {};
    if (!yields) return out;
    for (const t of Object.keys(yields)) {
      const y = yields[t];
      if (!y) continue;
      out[t] = y.accrual != null
        ? y.accrual
        : (y.price != null && y.clean_price != null ? y.price - y.clean_price : null);
    }
    return out;
  }, [yields]);

  const staleSinceByTicker = useMemo(() => staleTickers(yields), [yields]);

  // Notebook columns: cheap-bp and rank come from the live yields and each
  // ticker's historical mean (matrix payload); the $ view measures them on YTC.
  const basis = yieldBasis(spreadType);
  const cheapRank = useMemo(
    () => cheapAndRank(tickers, livePrices, yields, basis),
    [tickers, livePrices, yields, basis]
  );
  const coverageByTicker = useMemo(() => {
    const out = {};
    if (yields) for (const t of Object.keys(yields)) out[t] = coverageTitle(yields[t]?.hist);
    return out;
  }, [yields]);

  // Sorting state
  const [sortBy, setSortBy] = useState(null); // 'price', 'ytm', 'ytc', or null
  const [sortDirection, setSortDirection] = useState('desc');

  // Live cell values/percentiles, with unchanged rows keeping their identity.
  const { stableTickers, matrixData } = useLiveMatrixData(tickers, matrix, livePrices, getScaleFactor);

  // Memoize handlers to prevent row re-renders
  const handleCellClick = useCallback((ticker1, ticker2) => {
    if (ticker1 !== ticker2 && onCellClick) {
      onCellClick(ticker1, ticker2);
    }
  }, [onCellClick]);

  const formatSpread = useCallback((value, type = spreadType) => {
    if (value === null || value === undefined) return '-';

    switch (type) {
      case 'dollar':
        return `$${value.toFixed(2)}`;
      case 'percentile':
        return `${value.toFixed(1)}%`;
      case 'ytc':
      case 'spot-yield':
        // YTM/YTC spreads - value is in percentage points (e.g., 0.73)
        // Convert to basis points for display (0.73 * 100 = 73bp)
        return `${(value * 100).toFixed(0)}bp`;
      default:
        return value.toFixed(2);
    }
  }, [spreadType]);

  // Number-only formatter (no $ or bp) for the p5/p50/p95 tuple.
  // For yield spreads, values are stored as percentage points (e.g. 0.73 = 73bp).
  // We multiply by 100 so the displayed numbers are basis points (matches the
  // bp scale used for the current value) but without the bp suffix.
  const formatNumber = useCallback((value, type = spreadType) => {
    if (value === null || value === undefined) return '-';
    switch (type) {
      case 'dollar':
        return value.toFixed(2);
      case 'percentile':
        return value.toFixed(1);
      case 'ytc':
      case 'spot-yield':
        return (value * 100).toFixed(0);
      default:
        return value.toFixed(2);
    }
  }, [spreadType]);

  // Get the spread field based on spreadType - memoized
  const getSpreadField = useCallback((cellData) => {
    if (!cellData) return null;
    switch (spreadType) {
      case 'dollar':
      case 'percentile':
        return cellData.dollar_spread;
      case 'ytc':
        return cellData.ytc_spread;
      case 'spot-yield':
        // Use ytm_spread for YTM view (matches chart data), fallback to spot_yield_spread
        return cellData.ytm_spread || cellData.spot_yield_spread || cellData.yield_spread;
      default:
        return cellData.dollar_spread;
    }
  }, [spreadType]);

  // Get the appropriate spread value based on spreadType - memoized
  const getSpreadValue = useCallback((cellData) => {
    const spreadField = getSpreadField(cellData);
    if (!spreadField) return null;

    // For percentile view, return the percentile value
    if (spreadType === 'percentile') {
      return spreadField.percentile;
    }

    // For other views, return the current value
    return spreadField.current;
  }, [spreadType, getSpreadField]);

  // Get percentile for cell coloring - memoized
  const getCellPercentile = useCallback((cellData) => {
    const spreadField = getSpreadField(cellData);
    return spreadField?.percentile ?? null;
  }, [getSpreadField]);

  // z of one cell on the active view: yield views from the pair's mean/std and the
  // live spread, the $ view from the server's synthetic-series z.
  const getCellZ = useCallback((cellData) => (
    spreadType === 'dollar' || spreadType === 'percentile'
      ? dollarZ(cellData?.dollar_spread)
      : pairZ(getSpreadField(cellData))
  ), [spreadType, getSpreadField]);

  // Text color based on theme - stable reference
  const textColor = theme.palette.text.primary;

  // Handle sort toggle
  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, start with descending
      setSortBy(column);
      setSortDirection('desc');
    }
  }, [sortBy]);

  // Same Med% the rows display, so sorting orders rows by the shown number.
  const getMedianPercentile = useCallback(
    (ticker) => medianRowPercentile(ticker, stableTickers, matrixData[ticker], getCellPercentile),
    [stableTickers, matrixData, getCellPercentile]
  );
  const getMedianZ = useCallback(
    (ticker) => medianRowZ(ticker, stableTickers, matrixData[ticker], getCellZ),
    [stableTickers, matrixData, getCellZ]
  );

  // Sort tickers based on current sort settings
  const sortedTickers = useMemo(() => {
    if (!sortBy) return stableTickers;

    return [...stableTickers].sort((a, b) => {
      const priceA = livePrices[a];
      const priceB = livePrices[b];

      let valA, valB;
      switch (sortBy) {
        case 'price':
          valA = priceA?.price ?? 0;
          valB = priceB?.price ?? 0;
          break;
        case 'strip':
          // Strip = clean price (market price - accrual)
          valA = priceA?.cleanPrice ?? 0;
          valB = priceB?.cleanPrice ?? 0;
          break;
        case 'medPerc':
          valA = getMedianPercentile(a) ?? 50;  // Default to 50% if no data
          valB = getMedianPercentile(b) ?? 50;
          break;
        case 'deltaMed':
          valA = medPercDelta(getMedianPercentile(a), referenceMedPerc?.[a]) ?? 0;
          valB = medPercDelta(getMedianPercentile(b), referenceMedPerc?.[b]) ?? 0;
          break;
        // The notebook columns: rows without a value stay at the bottom either way.
        case 'medZ':
          return compareNullsLast(getMedianZ(a), getMedianZ(b), sortDirection);
        case 'cheap':
          return compareNullsLast(cheapRank.cheapBp[a], cheapRank.cheapBp[b], sortDirection);
        case 'rank':
          // Descending shows rank 1 first, the same order as Cheap bp descending.
          return compareNullsLast(
            cheapRank.rank[a] == null ? null : -cheapRank.rank[a],
            cheapRank.rank[b] == null ? null : -cheapRank.rank[b],
            sortDirection
          );
        default:
          return 0;
      }

      const direction = sortDirection === 'asc' ? 1 : -1;
      return (valA - valB) * direction;
    });
  }, [stableTickers, sortBy, sortDirection, livePrices, getMedianPercentile, referenceMedPerc, getMedianZ, cheapRank]);

  // Scroll to highlighted cell when search changes
  useEffect(() => {
    if (searchRow && searchCol && tableContainerRef.current) {
      // Find the cell element
      const cell = tableContainerRef.current.querySelector(
        `[data-row="${searchRow}"][data-col="${searchCol}"]`
      );
      if (cell) {
        cell.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }
  }, [searchRow, searchCol]);

  return (
    <TableContainer
      ref={tableContainerRef}
      component={Paper}
      // Automatic sizing: every var(--mx-*) read by the styled() cells below
      // resolves from here, so a resize re-sizes the grid without generating a
      // new Emotion class for each of the N^2 cells.
      style={metrics.cssVars}
      sx={{
        height: '100%',  // Fill parent container height
        maxHeight: '100%',
        overflow: 'auto',
        contain: 'strict', // CSS containment for entire container
        '&::-webkit-scrollbar': {
          height: 8,
          width: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'grey.100',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'grey.400',
          borderRadius: 4,
          '&:hover': {
            backgroundColor: 'grey.500',
          },
        },
      }}
    >
      <Table stickyHeader size="small" sx={{ minWidth: 'max-content' }}>
        <TableHead>
          <TableRow>
            {/* Column 0: Ticker header - sticky top+left */}
            <TickerHeaderCell align="center">
              Ticker
            </TickerHeaderCell>
            {/* Column 1: Price (sortable) - sticky top+left */}
            <SortableHeaderCell
              align="center"
              onClick={() => handleSort('price')}
              sx={{ left: PRICE_LEFT }}
            >
              <TableSortLabel
                active={sortBy === 'price'}
                direction={sortBy === 'price' ? sortDirection : 'desc'}
                sx={{ '& .MuiTableSortLabel-icon': { fontSize: FONT_HEADER } }}
              >
                Price
              </TableSortLabel>
            </SortableHeaderCell>
            {/* Column 2: Strip (sortable) - sticky top+left */}
            <SortableHeaderCell
              align="center"
              onClick={() => handleSort('strip')}
              sx={{ left: STRIP_LEFT }}
            >
              <TableSortLabel
                active={sortBy === 'strip'}
                direction={sortBy === 'strip' ? sortDirection : 'desc'}
                sx={{ '& .MuiTableSortLabel-icon': { fontSize: FONT_HEADER } }}
              >
                Strip
              </TableSortLabel>
            </SortableHeaderCell>
            {/* Column 3: Med% (sortable) - sticky top+left */}
            <SortableHeaderCell
              align="center"
              onClick={() => handleSort('medPerc')}
              sx={{ left: MEDPERC_LEFT }}
            >
              <TableSortLabel
                active={sortBy === 'medPerc'}
                direction={sortBy === 'medPerc' ? sortDirection : 'desc'}
                sx={{ '& .MuiTableSortLabel-icon': { fontSize: FONT_HEADER } }}
              >
                Med%
              </TableSortLabel>
            </SortableHeaderCell>
            {/* Column 4: Δ Med% (sortable) - sticky top+left */}
            <SortableHeaderCell
              align="center"
              onClick={() => handleSort('deltaMed')}
              title={deltaTitle(referenceSession)}
              sx={{ left: DELTA_LEFT }}
            >
              <TableSortLabel
                active={sortBy === 'deltaMed'}
                direction={sortBy === 'deltaMed' ? sortDirection : 'desc'}
                sx={{ '& .MuiTableSortLabel-icon': { fontSize: FONT_HEADER } }}
              >
                ΔMed
              </TableSortLabel>
            </SortableHeaderCell>
            {/* Column 5: Med Z (sortable) - sticky top+left */}
            <SortableHeaderCell
              align="center"
              onClick={() => handleSort('medZ')}
              title={medZTitle(spreadType)}
              sx={{ left: MEDZ_LEFT }}
            >
              <TableSortLabel
                active={sortBy === 'medZ'}
                direction={sortBy === 'medZ' ? sortDirection : 'desc'}
                sx={{ '& .MuiTableSortLabel-icon': { fontSize: FONT_HEADER } }}
              >
                MedZ
              </TableSortLabel>
            </SortableHeaderCell>
            {/* Column 6: Cheap bp (sortable) - sticky top+left */}
            <SortableHeaderCell
              align="center"
              onClick={() => handleSort('cheap')}
              title={cheapTitle(basis)}
              sx={{ left: CHEAP_LEFT }}
            >
              <TableSortLabel
                active={sortBy === 'cheap'}
                direction={sortBy === 'cheap' ? sortDirection : 'desc'}
                sx={{ '& .MuiTableSortLabel-icon': { fontSize: FONT_HEADER } }}
              >
                Cheap
              </TableSortLabel>
            </SortableHeaderCell>
            {/* Column 7: Rank (sortable) - sticky top+left */}
            <SortableHeaderCell
              align="center"
              onClick={() => handleSort('rank')}
              title={rankTitle(basis)}
              sx={{ left: RANK_LEFT, borderRight: '2px solid #ccc' }}
            >
              <TableSortLabel
                active={sortBy === 'rank'}
                direction={sortBy === 'rank' ? sortDirection : 'desc'}
                sx={{ '& .MuiTableSortLabel-icon': { fontSize: FONT_HEADER } }}
              >
                Rank
              </TableSortLabel>
            </SortableHeaderCell>
            {/* Columns 8+: Matrix ticker columns */}
            {sortedTickers.map(ticker => (
              <HeaderCell
                key={ticker}
                align="center"
                sx={{
                  // Highlight column header when it's the search column
                  ...(searchCol === ticker && {
                    outline: '2px solid #1976d2',
                    outlineOffset: '-2px',
                    backgroundColor: 'rgba(25, 118, 210, 0.15)',
                  }),
                }}
              >
                {ticker}
              </HeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Using memoized MatrixRow - each row only re-renders when its data changes */}
          {sortedTickers.map(ticker1 => (
            <MatrixRow
              key={ticker1}
              ticker1={ticker1}
              tickers={sortedTickers}
              matrixData={matrixData}
              priceData={livePrices[ticker1]}
              accrual={livePrices[ticker1]?.accrual ?? accrualByTicker[ticker1]}
              isDarkMode={isDarkMode}
              spreadType={spreadType}
              onCellClick={handleCellClick}
              formatSpread={formatSpread}
              formatNumber={formatNumber}
              getSpreadValue={getSpreadValue}
              getCellPercentile={getCellPercentile}
              getSpreadField={getSpreadField}
              textColor={textColor}
              highlightRow={searchRow}
              highlightCol={searchCol}
              referenceMed={referenceMedPerc?.[ticker1] ?? null}
              referenceSession={referenceSession}
              staleSince={staleSinceByTicker[ticker1]}
              getCellZ={getCellZ}
              cheapBp={cheapRank.cheapBp[ticker1]}
              rank={cheapRank.rank[ticker1]}
              basis={basis}
              coverage={coverageByTicker[ticker1]}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Memoize to prevent re-renders when parent state changes but our props haven't
export default memo(SpreadMatrix);